<div class="dialog-shell">
  <header mat-dialog-title class="header">
    <div class="header__text">
      <h2 class="header__title">{{ 'KNOWLEDGE_SHARING.SHARE.TITLE' | translate }}</h2>
      <p class="header__subtitle">{{ data.kbName }}</p>
    </div>
  </header>

  <mat-dialog-content class="content">
    @if (isLoading()) {
      <div class="loading">
        <glxy-loading-spinner></glxy-loading-spinner>
      </div>
    } @else {
      <!-- Add-only mode (opened from the detail card) hides this list; it's kept when opened from a table row. -->
      @if (!data.hideCurrentShares) {
      <section class="section">
        <h3 class="section__title">
          @if (currentGrantsLoadError()) {
            {{ 'KNOWLEDGE_SHARING.SHARE.CURRENT_SHARES' | translate }}
          } @else {
            {{ 'KNOWLEDGE_SHARING.SHARE.CURRENT_SHARES_COUNT' | translate: { count: currentGrants().length } }}
          }
        </h3>
        @if (currentGrantsLoadError(); as err) {
          <div class="load-error">
            <mat-icon class="load-error__icon">error_outline</mat-icon>
            <span>{{ err | translate }} {{ 'KNOWLEDGE_SHARING.SHARE.LOAD_ERROR_SUFFIX' | translate }}</span>
          </div>
        } @else if (currentGrants().length === 0) {
          <div class="empty">{{ 'KNOWLEDGE_SHARING.SHARE.NO_SHARES' | translate }}</div>
        } @else {
          <ul class="grants">
            @for (grant of currentGrants(); track grant.grantId) {
              <li
                class="grants__row"
                [class.grants__row--blocked]="blockedRevoke()?.grantId === grant.grantId"
              >
                <div class="grants__label">
                  <mat-icon class="grants__icon">{{ targetIconFor(grant.target.namespaceType) }}</mat-icon>
                  <div class="grants__text">
                    <div class="grants__name">{{ grant.target.displayName }}</div>
                    @if (grant.target.subtitle) {
                      <div class="grants__sub">{{ grant.target.subtitle }}</div>
                    }
                  </div>
                </div>
                @if (blockedRevoke()?.grantId !== grant.grantId) {
                  <button
                    mat-icon-button
                    class="grants__revoke"
                    [attr.aria-label]="'KNOWLEDGE_SHARING.SHARE.REVOKE_SHARE' | translate"
                    [matTooltip]="'KNOWLEDGE_SHARING.SHARE.REVOKE_SHARE' | translate"
                    [disabled]="revokingGrantId() === grant.grantId"
                    (click)="revokeGrant(grant)"
                  >
                    @if (revokingGrantId() === grant.grantId) {
                      <glxy-loading-spinner></glxy-loading-spinner>
                    } @else {
                      <mat-icon>delete_outline</mat-icon>
                    }
                  </button>
                }
              </li>
              @if (blockedRevoke(); as blocked) {
                @if (blocked.grantId === grant.grantId) {
                  <li class="blocked">
                    <div class="blocked__head">
                      <mat-icon class="blocked__warn">warning</mat-icon>
                      <span class="blocked__title">{{ 'KNOWLEDGE_SHARING.SHARE.BLOCKED_TITLE' | translate }}</span>
                    </div>
                    <ul class="blocked__deps">
                      @if (blocked.dependents.appAssociationCount > 0) {
                        <li class="blocked__dep">
                          <span class="blocked__dep-text">
                            {{
                              (blocked.dependents.appAssociationCount === 1
                                ? 'KNOWLEDGE_SHARING.SHARE.APPS_USING_ONE'
                                : 'KNOWLEDGE_SHARING.SHARE.APPS_USING_OTHER'
                              ) | translate: { count: blocked.dependents.appAssociationCount }
                            }}
                            @if (blocked.dependents.sampleAppAssociations?.length) {
                              ({{ blocked.dependents.sampleAppAssociations!.join(', ') }})
                            }
                          </span>
                          @if (blocked.pendingConfirm === 'apps') {
                            <span class="blocked__confirm">
                              {{
                                (blocked.dependents.appAssociationCount === 1
                                  ? 'KNOWLEDGE_SHARING.SHARE.DETACH_APPS_CONFIRM_ONE'
                                  : 'KNOWLEDGE_SHARING.SHARE.DETACH_APPS_CONFIRM_OTHER'
                                ) | translate: { count: blocked.dependents.appAssociationCount }
                              }}
                              <button
                                mat-flat-button
                                color="warn"
                                [disabled]="revokingGrantId() === grant.grantId"
                                (click)="confirmDetachApps(grant)"
                              >
                                {{ 'KNOWLEDGE_SHARING.SHARE.CONFIRM' | translate }}
                              </button>
                              <button mat-button (click)="cancelConfirm()">{{ 'COMMON.CANCEL' | translate }}</button>
                            </span>
                          } @else {
                            <button mat-stroked-button (click)="flagConfirm('apps')">
                              {{
                                (blocked.dependents.appAssociationCount === 1
                                  ? 'KNOWLEDGE_SHARING.SHARE.DETACH_APPS_ONE'
                                  : 'KNOWLEDGE_SHARING.SHARE.DETACH_APPS_OTHER'
                                ) | translate: { count: blocked.dependents.appAssociationCount }
                              }}
                            </button>
                          }
                        </li>
                      }
                      @if (blocked.dependents.downstreamGrantCount > 0) {
                        <li class="blocked__dep">
                          <span class="blocked__dep-text">
                            {{
                              (blocked.dependents.downstreamGrantCount === 1
                                ? 'KNOWLEDGE_SHARING.SHARE.DOWNSTREAM_ONE'
                                : 'KNOWLEDGE_SHARING.SHARE.DOWNSTREAM_OTHER'
                              ) | translate: { count: blocked.dependents.downstreamGrantCount }
                            }}
                            @if (blocked.dependents.sampleDownstreamGrants?.length) {
                              ({{ blocked.dependents.sampleDownstreamGrants!.join(', ') }})
                            }
                          </span>
                          @if (blocked.pendingConfirm === 'downstream') {
                            <span class="blocked__confirm">
                              {{
                                (blocked.dependents.downstreamGrantCount === 1
                                  ? 'KNOWLEDGE_SHARING.SHARE.REVOKE_DOWNSTREAM_CONFIRM_ONE'
                                  : 'KNOWLEDGE_SHARING.SHARE.REVOKE_DOWNSTREAM_CONFIRM_OTHER'
                                ) | translate: { count: blocked.dependents.downstreamGrantCount }
                              }}
                              <button
                                mat-flat-button
                                color="warn"
                                [disabled]="revokingGrantId() === grant.grantId"
                                (click)="confirmRevokeDownstream(grant)"
                              >
                                {{ 'KNOWLEDGE_SHARING.SHARE.CONFIRM' | translate }}
                              </button>
                              <button mat-button (click)="cancelConfirm()">{{ 'COMMON.CANCEL' | translate }}</button>
                            </span>
                          } @else {
                            <button mat-stroked-button (click)="flagConfirm('downstream')">
                              {{ 'KNOWLEDGE_SHARING.SHARE.REVOKE_DOWNSTREAM' | translate: { count: blocked.dependents.downstreamGrantCount } }}
                            </button>
                          }
                        </li>
                      }
                    </ul>
                    <div class="blocked__actions">
                      <button mat-button (click)="cancelBlockedRevoke()">{{ 'KNOWLEDGE_SHARING.SHARE.CANCEL_REVOKE' | translate }}</button>
                    </div>
                  </li>
                }
              }
            }
          </ul>
        }
      </section>

      <mat-divider></mat-divider>
      }

      <section class="section">
        <h3 class="section__title">{{ 'KNOWLEDGE_SHARING.SHARE.ADD_TARGETS' | translate }}</h3>

        @if (targetTypeOptions().length === 0) {
          <div class="empty">
            {{ 'KNOWLEDGE_SHARING.SHARE.READONLY' | translate }}
          </div>
        } @else {
          <div class="picker__controls">
            <glxy-form-field class="picker__type" bottomSpacing="none" [showLabel]="false">
              <glxy-label>{{ 'KNOWLEDGE_SHARING.SHARE.TARGET_TYPE' | translate }}</glxy-label>
              <mat-select
                [value]="targetType()"
                (selectionChange)="setTargetType($event.value)"
              >
                @for (opt of targetTypeOptions(); track opt.value) {
                  <mat-option [value]="opt.value">{{ opt.label | translate }}</mat-option>
                }
              </mat-select>
            </glxy-form-field>

            @if (showSearch()) {
              <glxy-form-field class="picker__search" suffixIcon="search" bottomSpacing="none" [showLabel]="false">
                <glxy-label>{{ 'COMMON.SEARCH' | translate }}</glxy-label>
                <input
                  matInput
                  [placeholder]="'KNOWLEDGE_SHARING.SHARE.SEARCH_PLACEHOLDER' | translate"
                  [ngModel]="search()"
                  (ngModelChange)="onSearchChange($event)"
                />
              </glxy-form-field>
            }
          </div>

          @if (isLookupInFlight()) {
            <div class="loading loading--inline">
              <glxy-loading-spinner></glxy-loading-spinner>
            </div>
          } @else {
            <ul class="picker__list">
              @for (opt of availableOptions(); track opt.identityKey) {
                <li
                  class="picker__row"
                  [class.picker__row--disabled]="currentTargetKeys().has(opt.identityKey)"
                >
                  <mat-checkbox
                    color="primary"
                    [checked]="isSelected(opt)"
                    [disabled]="currentTargetKeys().has(opt.identityKey)"
                    (change)="toggleTarget(opt, $event.checked)"
                  >
                    <div class="picker__label">
                      <div class="picker__name">{{ opt.displayName }}</div>
                      @if (opt.subtitle) {
                        <div class="picker__sub">{{ opt.subtitle }}</div>
                      }
                    </div>
                  </mat-checkbox>
                </li>
              } @empty {
                <li class="picker__empty">{{ 'KNOWLEDGE_SHARING.SHARE.NO_MATCHES' | translate }}</li>
              }
            </ul>
          }
        }
      </section>
    }
  </mat-dialog-content>

  <mat-dialog-actions align="end" class="actions">
    <span class="actions__count">
      {{
        (selectedCount() === 0
          ? 'KNOWLEDGE_SHARING.SHARE.NO_TARGETS_SELECTED'
          : 'KNOWLEDGE_SHARING.SHARE.TARGETS_SELECTED'
        ) | translate: { count: selectedCount() }
      }}
    </span>
    <button mat-button (click)="close()" [disabled]="isApplying()">{{ 'COMMON.CLOSE' | translate }}</button>
    <button
      mat-flat-button
      color="primary"
      [disabled]="selectedCount() === 0 || isApplying()"
      (click)="apply()"
    >
      @if (isApplying()) {
        {{ 'KNOWLEDGE_SHARING.SHARE.SHARING' | translate }}
      } @else {
        {{ 'KNOWLEDGE_SHARING.SHARE.APPLY' | translate }}
      }
    </button>
  </mat-dialog-actions>
</div>
